import { loadBaseCSS } from '../shared/button-utils.js';
import { sanitizeUrl } from '../shared/url-utils.js';
import '../action-panel/qds-action-panel.js';
import '../code-block/qds-code-block.js';
import '../inline-rich-text/qds-inline-rich-text.js';
import '../inline-text-editor/qds-inline-text-editor.js';
import type {
  QdsBlockLayout,
  QdsBlockVisibility,
  QdsCanvasState,
  QdsContentBlockInstance,
  QdsContentBreakpoint,
  QdsContentDocument,
  QdsContentLayout,
  QdsContentMode
} from '../../types/content.js';
import type {
  QdsBlockChildOptions,
  QdsBlockDefinition,
  QdsBlockRegistry,
  QdsConfigField,
  QdsHostBlockElement,
  QdsRenderContext,
  QdsRenderMode
} from '../../types/blocks.js';
import { applyTypographyStyle } from '../shared/typography-utils.js';

loadBaseCSS();

const DEFAULT_LAYOUT: Required<QdsContentLayout> = {
  columns: 12,
  rowHeight: 128,
  gap: '16px',
  maxWidth: 'min(1400px, 100%)'
};

const DEFAULT_CANVAS: Required<QdsCanvasState> = {
  zoom: 100,
  snapToGrid: true,
  showGrid: true,
  breakpoint: 'desktop'
};

const EMPTY_VISIBILITY: QdsBlockVisibility = {
  hidden: false,
  responsive: ['desktop', 'tablet', 'mobile']
};

let blockIdCounter = 0;

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
}

function toStringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function createHeadingLevelOptions(): Array<{ value: string; label: string }> {
  return ['1', '2', '3', '4', '5', '6'].map((value) => ({ value, label: `H${value}` }));
}

function createCodeLanguageOptions(): Array<{ value: string; label: string }> {
  return [
    { value: 'text', label: 'Text' },
    { value: 'bash', label: 'Bash' },
    { value: 'python', label: 'Python' },
    { value: 'sql', label: 'SQL' },
    { value: 'json', label: 'JSON' },
    { value: 'yaml', label: 'YAML' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'html', label: 'HTML' },
    { value: 'css', label: 'CSS' },
    { value: 'xml', label: 'XML' }
  ];
}

function withFontPicker(
  field: QdsConfigField,
  typographyKey: string
): QdsConfigField {
  return {
    ...field,
    typographyKey
  };
}

/**
 * Attaches a host-agnostic semantic `valueRole` hint to a config field.
 *
 * `type` describes how the value is stored/edited; `valueRole` describes what
 * the value means so a host can attach a role-specific editor once per role
 * instead of patching individual field names. QDS attaches no behavior to it.
 */
function withValueRole(
  field: QdsConfigField,
  valueRole: string
): QdsConfigField {
  return {
    ...field,
    valueRole
  };
}

function createFontOverrideField(
  displayName: string,
  previewProperty: string
): QdsConfigField {
  return {
    displayName: `${displayName} Font`,
    type: 'typography',
    hidden: true,
    editorOptions: {
      previewProperty
    }
  };
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Resolves a link through the host-supplied resolver (if any), then strips
 * unsafe URL schemes (e.g. `javascript:`). Returns an empty string for values
 * that are unsafe, so callers can decide whether to render an anchor at all.
 */
export function resolveSafeLink(value: string, context?: QdsRenderContext): string {
  if (!value) {
    return '';
  }
  const resolved = context?.resolveLink ? context.resolveLink(value) : value;
  return sanitizeUrl(resolved);
}

export function sanitizeHtml(html: string): string {
  if (!html || typeof document === 'undefined') {
    return html || '';
  }

  const template = document.createElement('template');
  template.innerHTML = html;
  const blockedTags = new Set(['script', 'iframe', 'object', 'embed', 'link', 'meta']);

  const cleanNode = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      if (blockedTags.has(element.tagName.toLowerCase())) {
        element.remove();
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value.trim().toLowerCase();

        if (name.startsWith('on')) {
          element.removeAttribute(attribute.name);
          return;
        }

        if ((name === 'href' || name === 'src') && value.startsWith('javascript:')) {
          element.removeAttribute(attribute.name);
        }
      });
    }

    Array.from(node.childNodes).forEach((child) => cleanNode(child));
  };

  Array.from(template.content.childNodes).forEach((child) => cleanNode(child));
  return template.innerHTML;
}

function normalizeVisibility(visibility?: QdsBlockVisibility): QdsBlockVisibility {
  return {
    hidden: Boolean(visibility?.hidden),
    responsive: Array.isArray(visibility?.responsive) && visibility?.responsive.length > 0
      ? [...visibility.responsive]
      : ['desktop', 'tablet', 'mobile']
  };
}

function normalizeParentBlockId(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

export function getBlockChildOptions(
  definition: QdsBlockDefinition | undefined
): QdsBlockChildOptions | undefined {
  if (!definition?.capabilities?.supportsChildren && !definition?.childOptions) {
    return undefined;
  }

  return {
    accepts: Array.isArray(definition?.childOptions?.accepts)
      ? [...(definition?.childOptions?.accepts || [])]
      : undefined,
    layout: definition?.childOptions?.layout || 'flow',
    emptyLabel: definition?.childOptions?.emptyLabel || 'Drop components here.'
  };
}

export function blockSupportsChildren(
  blockOrDefinition: QdsContentBlockInstance | QdsBlockDefinition | undefined,
  registry?: QdsBlockRegistry | null
): boolean {
  if (!blockOrDefinition) {
    return false;
  }

  const definition = 'defaultConfig' in blockOrDefinition
    ? blockOrDefinition
    : resolveRegistry(registry).get(blockOrDefinition.type);

  return Boolean(definition?.capabilities?.supportsChildren || definition?.childOptions);
}

export function getViewportBreakpoint(width: number): QdsContentBreakpoint {
  if (width <= 1200) {
    return 'mobile';
  }

  if (width <= 1400) {
    return 'tablet';
  }

  return 'desktop';
}

export function isBlockVisible(
  block: QdsContentBlockInstance,
  breakpoint: QdsContentBreakpoint
): boolean {
  const visibility = normalizeVisibility(block.visibility);
  if (visibility.hidden) {
    return false;
  }

  return (visibility.responsive || ['desktop', 'tablet', 'mobile']).includes(breakpoint);
}

export function createBlockId(prefix = 'qds-block'): string {
  blockIdCounter += 1;
  return `${prefix}-${Date.now()}-${blockIdCounter}`;
}

function createLegacyCardGridChildren(
  block: Partial<QdsContentBlockInstance>
): Array<Partial<QdsContentBlockInstance>> {
  const cards = parseStructuredConfigValue<Array<Record<string, unknown>>>(block.config?.cards, []);

  return cards.map((card) => ({
    id: createBlockId('card-basic'),
    type: 'card-basic',
    title: toStringValue(card.title, 'Card'),
    config: {
      title: toStringValue(card.title, 'Card'),
      description: toStringValue(card.description),
      buttonText: toStringValue(card.buttonText, toStringValue(card.href) ? 'Open' : 'Details'),
      href: toStringValue(card.href),
      target: '_self',
      icon: ''
    }
  }));
}

function createDefaultDashboardLayout(
  definition: QdsBlockDefinition,
  index: number,
  columns: number
): QdsBlockLayout {
  const width = Math.min(definition.sizeHints?.w || 4, columns);
  const height = definition.sizeHints?.h || 2;
  const columnsPerRow = Math.max(1, Math.floor(columns / Math.max(width, 1)));
  const x = (index % columnsPerRow) * width;
  const y = Math.floor(index / columnsPerRow) * height;

  return {
    x,
    y,
    w: width,
    h: height,
    minW: definition.sizeHints?.minW,
    minH: definition.sizeHints?.minH,
    maxW: definition.sizeHints?.maxW,
    maxH: definition.sizeHints?.maxH,
    order: index
  };
}

function mergeLayout(
  mode: QdsContentMode,
  layout: QdsBlockLayout | undefined,
  definition: QdsBlockDefinition,
  index: number,
  columns: number
): QdsBlockLayout {
  if (mode === 'dashboard') {
    const defaults = createDefaultDashboardLayout(definition, index, columns);
    return {
      ...defaults,
      ...layout,
      w: Math.min(columns, toNumber(layout?.w, defaults.w || 4)),
      h: Math.max(1, toNumber(layout?.h, defaults.h || 2)),
      x: Math.max(0, toNumber(layout?.x, defaults.x || 0)),
      y: Math.max(0, toNumber(layout?.y, defaults.y || 0))
    };
  }

  return {
    order: toNumber(layout?.order, index)
  };
}

export interface QdsBlockTreeLocation {
  block: QdsContentBlockInstance;
  parentBlock: QdsContentBlockInstance | null;
  parentBlockId: string | null;
  siblings: QdsContentBlockInstance[];
  index: number;
  depth: number;
}

export function getBlockChildren(
  block: QdsContentBlockInstance | null | undefined,
  mode: QdsContentMode = 'page'
): QdsContentBlockInstance[] {
  return sortBlocksForMode(Array.isArray(block?.children) ? block.children : [], mode);
}

export function findBlockTreeLocation(
  blocks: QdsContentBlockInstance[],
  blockId: string,
  mode: QdsContentMode = 'page'
): QdsBlockTreeLocation | null {
  const visit = (
    siblings: QdsContentBlockInstance[],
    parentBlock: QdsContentBlockInstance | null,
    parentBlockId: string | null,
    depth: number
  ): QdsBlockTreeLocation | null => {
    const ordered = sortBlocksForMode(siblings, mode);

    for (let index = 0; index < ordered.length; index += 1) {
      const block = ordered[index];
      if (block.id === blockId) {
        return {
          block,
          parentBlock,
          parentBlockId,
          siblings,
          index: siblings.indexOf(block),
          depth
        };
      }

      const childMatch = visit(
        Array.isArray(block.children) ? block.children : [],
        block,
        block.id,
        depth + 1
      );
      if (childMatch) {
        return childMatch;
      }
    }

    return null;
  };

  return visit(blocks, null, null, 0);
}

export function findBlockById(
  blocks: QdsContentBlockInstance[],
  blockId: string,
  mode: QdsContentMode = 'page'
): QdsContentBlockInstance | null {
  return findBlockTreeLocation(blocks, blockId, mode)?.block || null;
}

export function findFirstBlock(
  blocks: QdsContentBlockInstance[],
  mode: QdsContentMode = 'page'
): QdsContentBlockInstance | null {
  const ordered = sortBlocksForMode(blocks, mode);
  if (ordered[0]) {
    return ordered[0];
  }

  return null;
}

export function countBlocksInTree(blocks: QdsContentBlockInstance[]): number {
  return blocks.reduce((count, block) => count + 1 + countBlocksInTree(block.children || []), 0);
}

function getSiblingsForParent(
  blocks: QdsContentBlockInstance[],
  parentBlockId: string | null,
  mode: QdsContentMode = 'page'
): QdsContentBlockInstance[] | null {
  const normalizedParentId = normalizeParentBlockId(parentBlockId);
  if (!normalizedParentId) {
    return blocks;
  }

  const parent = findBlockById(blocks, normalizedParentId, mode);
  if (!parent) {
    return null;
  }

  if (!Array.isArray(parent.children)) {
    parent.children = [];
  }

  return parent.children;
}

export function insertBlockAtTarget(
  blocks: QdsContentBlockInstance[],
  block: QdsContentBlockInstance,
  target: { parentBlockId: string | null; index: number },
  mode: QdsContentMode = 'page'
): boolean {
  const siblings = getSiblingsForParent(blocks, target.parentBlockId, mode);
  if (!siblings) {
    return false;
  }

  const boundedIndex = Math.max(0, Math.min(target.index, siblings.length));
  siblings.splice(boundedIndex, 0, block);
  return true;
}

export function removeBlockFromTree(
  blocks: QdsContentBlockInstance[],
  blockId: string,
  mode: QdsContentMode = 'page'
): (QdsBlockTreeLocation & { removedBlock: QdsContentBlockInstance }) | null {
  const location = findBlockTreeLocation(blocks, blockId, mode);
  if (!location) {
    return null;
  }

  const [removedBlock] = location.siblings.splice(location.index, 1);
  if (!removedBlock) {
    return null;
  }

  return {
    ...location,
    removedBlock
  };
}

export function updateBlockInTree(
  blocks: QdsContentBlockInstance[],
  blockId: string,
  updater: (block: QdsContentBlockInstance) => QdsContentBlockInstance,
  mode: QdsContentMode = 'page'
): boolean {
  const location = findBlockTreeLocation(blocks, blockId, mode);
  if (!location) {
    return false;
  }

  location.siblings.splice(location.index, 1, updater(location.block));
  return true;
}

export function createBuiltInBlockDefinitions(): QdsBlockDefinition[] {
  return [
    {
      type: 'rich-text',
      label: 'Rich Text',
      description: 'Formatted HTML content for introductions, summaries, or narrative sections.',
      category: 'Content',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        html: '<p>Start writing here.</p>'
      },
      configSchema: {
        html: {
          displayName: 'HTML',
          description: 'Rich HTML content for this block.',
          type: 'textarea'
        }
      },
      sizeHints: { w: 6, h: 2, minW: 3, minH: 1 },
      capabilities: {
        supportsRawHtml: true,
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'section',
      label: 'Section',
      description: 'A full-width container for grouping related content blocks.',
      category: 'Structure',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        eyebrow: 'Section',
        title: 'Section title'
      },
      configSchema: {
        eyebrow: withFontPicker({ displayName: 'Eyebrow', type: 'string' }, 'eyebrowFont'),
        eyebrowFont: createFontOverrideField('Eyebrow', 'eyebrow'),
        title: withFontPicker({ displayName: 'Title', type: 'string' }, 'titleFont'),
        titleFont: createFontOverrideField('Title', 'title')
      },
      sizeHints: { w: 12, h: 2, minW: 4, minH: 1 },
      capabilities: {
        supportsChildren: true,
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      },
      childOptions: {
        layout: 'flow',
        emptyLabel: 'Drop blocks into this section.'
      }
    },
    {
      type: 'split-section',
      label: 'Split Section',
      description: 'A two-column section with media on one side and nested content on the other.',
      category: 'Structure',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        mediaSrc: 'https://placehold.co/960x960?text=Split+Section',
        mediaAlt: 'Split section media',
        mediaPosition: 'right',
        mediaFit: 'cover'
      },
      configSchema: {
        mediaSrc: withValueRole({ displayName: 'Media URL', type: 'string' }, 'image-asset'),
        mediaAlt: { displayName: 'Media Alt Text', type: 'string' },
        mediaPosition: {
          displayName: 'Media Position',
          type: 'select',
          options: [
            { value: 'left', label: 'Left' },
            { value: 'right', label: 'Right' }
          ]
        },
        mediaFit: {
          displayName: 'Media Fit',
          type: 'select',
          options: [
            { value: 'cover', label: 'Cover' },
            { value: 'contain', label: 'Contain' }
          ]
        }
      },
      sizeHints: { w: 12, h: 4, minW: 4, minH: 3 },
      capabilities: {
        supportsChildren: true,
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      },
      childOptions: {
        layout: 'flow',
        emptyLabel: 'Drop blocks into the content side of this split section.'
      }
    },
    {
      type: 'heading',
      label: 'Heading',
      description: 'Standalone heading block with level and alignment options.',
      category: 'Content',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        text: 'Section heading',
        level: '2',
        align: 'left'
      },
      configSchema: {
        text: withFontPicker({ displayName: 'Text', type: 'string' }, 'textFont'),
        textFont: createFontOverrideField('Text', 'text'),
        level: {
          displayName: 'Level',
          type: 'select',
          options: createHeadingLevelOptions()
        },
        align: {
          displayName: 'Alignment',
          type: 'select',
          options: [
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' }
          ]
        }
      },
      sizeHints: { w: 6, h: 1, minW: 3, minH: 1 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'code-block',
      label: 'Code Block',
      description: 'A labeled code sample with a built-in copy-to-clipboard action.',
      category: 'Content',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        language: 'text',
        value: 'Paste code here.'
      },
      configSchema: {
        language: {
          displayName: 'Language',
          type: 'select',
          options: createCodeLanguageOptions()
        },
        value: {
          displayName: 'Code',
          description: 'Code content rendered in the block and copied to the clipboard.',
          type: 'textarea'
        }
      },
      sizeHints: { w: 8, h: 4, minW: 3, minH: 2 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'callout',
      label: 'Callout',
      description: 'Highlighted message box for important notes or guidance.',
      category: 'Content',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        tone: 'info',
        title: 'Important update',
        body: 'Use callouts to pull attention to supporting context.'
      },
      configSchema: {
        tone: {
          displayName: 'Tone',
          type: 'select',
          options: [
            { value: 'info', label: 'Info' },
            { value: 'success', label: 'Success' },
            { value: 'warning', label: 'Warning' },
            { value: 'critical', label: 'Critical' }
          ]
        },
        title: withFontPicker({ displayName: 'Title', type: 'string' }, 'titleFont'),
        titleFont: createFontOverrideField('Title', 'title'),
        body: withFontPicker({ displayName: 'Body', type: 'textarea' }, 'bodyFont'),
        bodyFont: createFontOverrideField('Body', 'body')
      },
      sizeHints: { w: 4, h: 2, minW: 3, minH: 1 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'hero',
      label: 'Hero',
      description: 'Large introduction banner with headline, supporting copy, media, and calls to action.',
      category: 'Content',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        eyebrow: 'Portfolio update',
        title: 'Lead with the one sentence that frames the whole page.',
        body: 'Use hero blocks when the document needs a strong opening statement, a supporting image, and one or two clear actions.',
        mediaSrc: 'https://placehold.co/960x640?text=Hero',
        mediaAlt: 'Hero illustration',
        primaryLabel: 'Open dashboard',
        primaryHref: '#dashboard',
        primaryTarget: '_self',
        secondaryLabel: 'Read memo',
        secondaryHref: '#memo',
        secondaryTarget: '_self',
        align: 'left'
      },
      configSchema: {
        eyebrow: withFontPicker({ displayName: 'Eyebrow', type: 'string' }, 'eyebrowFont'),
        eyebrowFont: createFontOverrideField('Eyebrow', 'eyebrow'),
        title: withFontPicker({ displayName: 'Title', type: 'string' }, 'titleFont'),
        titleFont: createFontOverrideField('Title', 'title'),
        body: withFontPicker({ displayName: 'Body', type: 'textarea' }, 'bodyFont'),
        bodyFont: createFontOverrideField('Body', 'body'),
        mediaSrc: withValueRole({ displayName: 'Media URL', type: 'string' }, 'image-asset'),
        mediaAlt: { displayName: 'Media Alt Text', type: 'string' },
        primaryLabel: withFontPicker({ displayName: 'Primary Label', type: 'string' }, 'primaryLabelFont'),
        primaryLabelFont: createFontOverrideField('Primary Label', 'primaryLabel'),
        primaryHref: withValueRole({ displayName: 'Primary Link', type: 'string' }, 'link-target'),
        primaryTarget: {
          displayName: 'Primary Target',
          type: 'select',
          options: [
            { value: '_self', label: 'Same Tab' },
            { value: '_blank', label: 'New Tab' }
          ]
        },
        secondaryLabel: withFontPicker({ displayName: 'Secondary Label', type: 'string' }, 'secondaryLabelFont'),
        secondaryLabelFont: createFontOverrideField('Secondary Label', 'secondaryLabel'),
        secondaryHref: withValueRole({ displayName: 'Secondary Link', type: 'string' }, 'link-target'),
        secondaryTarget: {
          displayName: 'Secondary Target',
          type: 'select',
          options: [
            { value: '_self', label: 'Same Tab' },
            { value: '_blank', label: 'New Tab' }
          ]
        },
        align: {
          displayName: 'Alignment',
          type: 'select',
          options: [
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' }
          ]
        }
      },
      sizeHints: { w: 12, h: 4, minW: 4, minH: 3 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'image',
      label: 'Image',
      description: 'Responsive image with optional caption and link.',
      category: 'Media',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        src: 'https://placehold.co/1200x675?text=QDS+Image',
        alt: 'Replace with descriptive alt text',
        caption: 'Optional supporting caption',
        fit: 'cover',
        href: ''
      },
      configSchema: {
        src: withValueRole({ displayName: 'Image URL', type: 'string' }, 'image-asset'),
        alt: { displayName: 'Alt Text', type: 'string' },
        caption: withFontPicker({ displayName: 'Caption', type: 'string' }, 'captionFont'),
        captionFont: createFontOverrideField('Caption', 'caption'),
        fit: {
          displayName: 'Object Fit',
          type: 'select',
          options: [
            { value: 'cover', label: 'Cover' },
            { value: 'contain', label: 'Contain' }
          ]
        },
        href: withValueRole({ displayName: 'Link URL', type: 'string' }, 'link-target')
      },
      sizeHints: { w: 6, h: 3, minW: 3, minH: 2 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'diagram',
      label: 'Diagram',
      description: 'Embeds a qds-diagram-host for architecture views, process maps, and system diagrams.',
      category: 'Media',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        diagram: '',
        path: '',
        drawingName: '',
        height: '420px',
        editable: false,
        libraries: 'Quantum'
      },
      configSchema: {
        diagram: {
          displayName: 'Diagram XML',
          description: 'Paste diagrams.net XML here, or leave blank and use a storage path plus drawing name.',
          type: 'textarea'
        },
        path: { displayName: 'Storage Path', type: 'string' },
        drawingName: { displayName: 'Drawing Name', type: 'string' },
        height: { displayName: 'Height', type: 'string' },
        editable: { displayName: 'Editable', type: 'boolean' },
        libraries: { displayName: 'Libraries', type: 'string' }
      },
      sizeHints: { w: 8, h: 4, minW: 4, minH: 3 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'gradient-line',
      label: 'Gradient Line',
      description: 'A horizontal gradient divider for separating groups of content.',
      category: 'Structure',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        startColor: 'var(--theme-success, var(--brand-green))',
        endColor: 'var(--theme-accent-primary, var(--brand-middle-blue))',
        height: 3
      },
      configSchema: {
        startColor: { displayName: 'Start Color', type: 'color' },
        endColor: { displayName: 'End Color', type: 'color' },
        height: { displayName: 'Height', type: 'number', min: 1, max: 12, step: 1 }
      },
      sizeHints: { w: 12, h: 1, minW: 3, minH: 1 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'action-panel',
      label: 'Action Panel',
      description: 'Large launch-style action tiles grouped in a single panel.',
      category: 'Navigation',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        title: 'Panel title',
        description: 'Add a short introduction that frames the actions below.',
        columns: 2,
        items: [
          {
            label: 'Open resource',
            href: '#',
            target: '_self',
            iconSrc: '',
            iconAlt: '',
            actionKind: 'forward'
          },
          {
            label: 'Download guide',
            href: '#',
            target: '_self',
            iconSrc: '',
            iconAlt: '',
            actionKind: 'download'
          }
        ]
      },
      configSchema: {
        title: withFontPicker({ displayName: 'Title', type: 'string' }, 'titleFont'),
        titleFont: createFontOverrideField('Title', 'title'),
        description: withFontPicker({ displayName: 'Description', type: 'textarea' }, 'descriptionFont'),
        descriptionFont: createFontOverrideField('Description', 'description'),
        columns: {
          displayName: 'Columns',
          type: 'number',
          min: 1,
          max: 4,
          step: 1
        },
        items: {
          displayName: 'Items',
          description: 'Manage the launch actions rendered inside the panel.',
          type: 'textarea',
          editorTag: 'qds-action-panel-items-editor'
        }
      },
      renderTag: 'qds-action-panel',
      sizeHints: { w: 10, h: 4, minW: 4, minH: 3 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'card-basic',
      label: 'Card Basic',
      description: 'A compact card with title, description, icon, and CTA.',
      category: 'Cards',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        title: 'Card title',
        description: 'Short description for this card.',
        icon: '',
        buttonText: 'Browse',
        href: '#',
        target: '_self'
      },
      configSchema: {
        title: withFontPicker({ displayName: 'Title', type: 'string' }, 'titleFont'),
        titleFont: createFontOverrideField('Title', 'title'),
        description: withFontPicker({ displayName: 'Description', type: 'textarea' }, 'descriptionFont'),
        descriptionFont: createFontOverrideField('Description', 'description'),
        icon: {
          displayName: 'Icon',
          description: 'Choose a PrimeIcons or Lucide icon for this card.',
          type: 'string',
          valueRole: 'icon',
          editorTag: 'qds-icon-picker'
        },
        buttonText: withFontPicker({ displayName: 'Button Text', type: 'string' }, 'buttonTextFont'),
        buttonTextFont: createFontOverrideField('Button Text', 'buttonText'),
        href: withValueRole({ displayName: 'Link URL', type: 'string' }, 'link-target'),
        target: {
          displayName: 'Link Target',
          type: 'select',
          options: [
            { value: '_self', label: 'Same Tab' },
            { value: '_blank', label: 'New Tab' }
          ]
        }
      },
      sizeHints: { w: 4, h: 3, minW: 3, minH: 2 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'card-advanced',
      label: 'Card Advanced',
      description: 'A richer card with media, copy, and CTA.',
      category: 'Cards',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        title: 'Card title',
        description: 'Short description for this card.',
        image: 'https://placehold.co/960x540?text=Card',
        buttonText: 'More',
        href: '#',
        target: '_self',
        imagePosition: 'top'
      },
      configSchema: {
        title: withFontPicker({ displayName: 'Title', type: 'string' }, 'titleFont'),
        titleFont: createFontOverrideField('Title', 'title'),
        description: withFontPicker({ displayName: 'Description', type: 'textarea' }, 'descriptionFont'),
        descriptionFont: createFontOverrideField('Description', 'description'),
        image: withValueRole({ displayName: 'Image URL', type: 'string' }, 'image-asset'),
        buttonText: withFontPicker({ displayName: 'Button Text', type: 'string' }, 'buttonTextFont'),
        buttonTextFont: createFontOverrideField('Button Text', 'buttonText'),
        href: withValueRole({ displayName: 'Link URL', type: 'string' }, 'link-target'),
        target: {
          displayName: 'Link Target',
          type: 'select',
          options: [
            { value: '_self', label: 'Same Tab' },
            { value: '_blank', label: 'New Tab' }
          ]
        },
        imagePosition: {
          displayName: 'Image Position',
          type: 'select',
          options: [
            { value: 'top', label: 'Top' },
            { value: 'bottom', label: 'Bottom' },
            { value: 'left', label: 'Left' },
            { value: 'right', label: 'Right' }
          ]
        }
      },
      sizeHints: { w: 4, h: 4, minW: 3, minH: 3 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'link-list',
      label: 'Link List',
      description: 'Curated set of links with optional descriptions.',
      category: 'Navigation',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        title: 'Quick links',
        links: [
          {
            label: 'Portfolio dashboard',
            href: '#',
            description: 'Open the shared metrics workspace.'
          },
          {
            label: 'Investment committee notes',
            href: '#',
            description: 'Review the latest discussion package.'
          }
        ]
      },
      configSchema: {
        title: withFontPicker({ displayName: 'Title', type: 'string' }, 'titleFont'),
        titleFont: createFontOverrideField('Title', 'title'),
        links: {
          displayName: 'Links (JSON)',
          description: 'Array of { label, href, description } objects.',
          type: 'textarea'
        }
      },
      sizeHints: { w: 4, h: 2, minW: 3, minH: 1 },
      capabilities: {
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      }
    },
    {
      type: 'card-grid',
      label: 'Card Grid',
      description: 'A responsive container that lays out nested cards in columns.',
      category: 'Layout',
      modes: ['page', 'dashboard'],
      defaultConfig: {
        title: 'Highlights',
        columns: 3
      },
      configSchema: {
        title: withFontPicker({ displayName: 'Title', type: 'string' }, 'titleFont'),
        titleFont: createFontOverrideField('Title', 'title'),
        columns: {
          displayName: 'Columns',
          type: 'number',
          min: 1,
          max: 4,
          step: 1
        }
      },
      sizeHints: { w: 8, h: 3, minW: 4, minH: 2 },
      capabilities: {
        supportsChildren: true,
        supportsDashboardPlacement: true,
        supportsPageFlow: true
      },
      childOptions: {
        accepts: ['card-basic', 'card-advanced'],
        layout: 'grid',
        emptyLabel: 'Drop cards into this grid.'
      }
    }
  ];
}

export class QdsContentRegistry implements QdsBlockRegistry {
  private readonly _definitions = new Map<string, QdsBlockDefinition>();

  constructor(
    definitions: QdsBlockDefinition[] = [],
    options: { includeBuiltIns?: boolean } = {}
  ) {
    const includeBuiltIns = options.includeBuiltIns !== false;
    const source = includeBuiltIns
      ? [...createBuiltInBlockDefinitions(), ...definitions]
      : [...definitions];

    source.forEach((definition) => {
      this.register(definition);
    });
  }

  register(definition: QdsBlockDefinition): this {
    this._definitions.set(definition.type, {
      ...definition,
      defaultConfig: deepClone(definition.defaultConfig || {}),
      configSchema: definition.configSchema ? { ...definition.configSchema } : undefined,
      modes: [...definition.modes],
      capabilities: definition.capabilities ? { ...definition.capabilities } : undefined,
      sizeHints: definition.sizeHints ? { ...definition.sizeHints } : undefined,
      childOptions: definition.childOptions
        ? {
            ...definition.childOptions,
            accepts: definition.childOptions.accepts ? [...definition.childOptions.accepts] : undefined
          }
        : undefined
    });

    return this;
  }

  get(type: string): QdsBlockDefinition | undefined {
    const definition = this._definitions.get(type);
    return definition ? deepClone(definition) : undefined;
  }

  list(): QdsBlockDefinition[] {
    return Array.from(this._definitions.values()).map((definition) => deepClone(definition));
  }

  listForMode(mode: QdsContentMode): QdsBlockDefinition[] {
    return this.list().filter((definition) => definition.modes.includes(mode));
  }
}

const defaultRegistry = new QdsContentRegistry();

export function resolveRegistry(registry?: QdsBlockRegistry | null): QdsBlockRegistry {
  return registry || defaultRegistry;
}

export function createBlockInstance(
  definition: QdsBlockDefinition,
  mode: QdsContentMode,
  index: number,
  columns = DEFAULT_LAYOUT.columns
): QdsContentBlockInstance {
  return {
    id: createBlockId(definition.type),
    type: definition.type,
    title: definition.label,
    config: deepClone(definition.defaultConfig || {}),
    children: definition.capabilities?.supportsChildren ? [] : undefined,
    layout: mergeLayout(mode, undefined, definition, index, columns),
    visibility: deepClone(EMPTY_VISIBILITY),
    version: 1
  };
}

export function normalizeBlockInstance(
  block: Partial<QdsContentBlockInstance>,
  index: number,
  mode: QdsContentMode,
  registry?: QdsBlockRegistry | null,
  columns = DEFAULT_LAYOUT.columns
): QdsContentBlockInstance {
  const resolvedRegistry = resolveRegistry(registry);
  const definition = resolvedRegistry.get(block.type || '') || {
    type: block.type || 'unknown',
    label: block.title || 'Unsupported Block',
    modes: ['page', 'dashboard'] as QdsContentMode[],
    defaultConfig: {},
    description: '',
    category: 'Unsupported'
  };
  const childSource = Array.isArray(block.children)
    ? block.children
    : block.type === 'card-grid'
      ? createLegacyCardGridChildren(block)
      : [];
  const normalizedChildren = childSource.map((child, childIndex) =>
    normalizeBlockInstance(child, childIndex, 'page', resolvedRegistry, columns)
  );

  return {
    id: block.id || createBlockId(definition.type),
    type: block.type || definition.type,
    title: block.title || definition.label,
    config: {
      ...deepClone(definition.defaultConfig || {}),
      ...(block.config || {})
    },
    children: normalizedChildren.length > 0 || blockSupportsChildren(definition, resolvedRegistry)
      ? normalizedChildren
      : undefined,
    layout: mergeLayout(mode, block.layout, definition, index, columns),
    visibility: normalizeVisibility(block.visibility),
    version: toNumber(block.version, 1)
  };
}

export function normalizeContentDocument(
  document: QdsContentDocument | null | undefined,
  registry?: QdsBlockRegistry | null
): QdsContentDocument {
  const resolvedRegistry = resolveRegistry(registry);
  const mode = document?.mode === 'dashboard' ? 'dashboard' : 'page';
  const layout = {
    ...DEFAULT_LAYOUT,
    ...(document?.layout || {})
  };

  return {
    version: toNumber(document?.version, 1),
    mode,
    title: toStringValue(document?.title),
    summary: toStringValue(document?.summary),
    bodyHtml: toStringValue(document?.bodyHtml),
    blocks: Array.isArray(document?.blocks)
      ? document.blocks.map((block, index) => normalizeBlockInstance(block, index, mode, resolvedRegistry, layout.columns))
      : [],
    layout,
    canvas: {
      ...DEFAULT_CANVAS,
      ...(document?.canvas || {})
    },
    metadata: document?.metadata ? deepClone(document.metadata) : {}
  };
}

export function cloneContentDocument<T>(document: T): T {
  return deepClone(document);
}

export function sortBlocksForMode(
  blocks: QdsContentBlockInstance[],
  mode: QdsContentMode
): QdsContentBlockInstance[] {
  const sorted = [...blocks];

  sorted.sort((left, right) => {
    if (mode === 'dashboard') {
      const leftY = toNumber(left.layout?.y, 0);
      const rightY = toNumber(right.layout?.y, 0);
      const leftX = toNumber(left.layout?.x, 0);
      const rightX = toNumber(right.layout?.x, 0);
      const leftOrder = toNumber(left.layout?.order, 0);
      const rightOrder = toNumber(right.layout?.order, 0);

      if (leftY !== rightY) {
        return leftY - rightY;
      }

      if (leftX !== rightX) {
        return leftX - rightX;
      }

      return leftOrder - rightOrder;
    }

    return toNumber(left.layout?.order, 0) - toNumber(right.layout?.order, 0);
  });

  return sorted;
}

export function resequencePageBlocks(blocks: QdsContentBlockInstance[]): QdsContentBlockInstance[] {
  return sortBlocksForMode(blocks, 'page').map((block, index) => ({
    ...block,
    children: Array.isArray(block.children) ? resequencePageBlocks(block.children) : undefined,
    layout: {
      ...(block.layout || {}),
      order: index
    }
  }));
}

export function ensureDashboardBounds(
  layout: QdsBlockLayout,
  columns: number
): QdsBlockLayout {
  const minW = Math.max(1, toNumber(layout.minW, 1));
  const minH = Math.max(1, toNumber(layout.minH, 1));
  const maxW = Math.max(minW, toNumber(layout.maxW, columns));
  const maxH = Math.max(minH, toNumber(layout.maxH, 12));
  const w = Math.max(minW, Math.min(maxW, toNumber(layout.w, minW)));
  const h = Math.max(minH, Math.min(maxH, toNumber(layout.h, minH)));
  const x = Math.max(0, Math.min(columns - w, toNumber(layout.x, 0)));
  const y = Math.max(0, toNumber(layout.y, 0));

  return {
    ...layout,
    minW,
    minH,
    maxW,
    maxH,
    w,
    h,
    x,
    y
  };
}

export function parseStructuredConfigValue<T>(value: unknown, fallback: T): T {
  if (Array.isArray(value) || (value && typeof value === 'object')) {
    return value as T;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    return fallback;
  }
}

function createTextNodeSurface(className: string, text: string): HTMLElement {
  const element = document.createElement('div');
  element.className = className;
  element.textContent = text;
  return element;
}

type InlineSurfaceEditorElement = HTMLElement & {
  property?: string;
  value?: string;
  placeholder?: string;
  multiline?: boolean;
  readonly?: boolean;
};

function shouldRenderCanvasEditor(
  renderMode: QdsRenderMode,
  selected = false,
  readonly = false
): boolean {
  return renderMode === 'design' && selected && !readonly;
}

function createInlineTextEditorElement(
  property: string,
  value: string,
  options: {
    placeholder?: string;
    multiline?: boolean;
  } = {}
): InlineSurfaceEditorElement {
  const editor = document.createElement('qds-inline-text-editor') as InlineSurfaceEditorElement;
  editor.property = property;
  editor.value = value;
  editor.placeholder = options.placeholder || '';
  editor.multiline = Boolean(options.multiline);
  return editor;
}

function createInlineRichTextEditorElement(
  property: string,
  value: string,
  options: {
    placeholder?: string;
  } = {}
): InlineSurfaceEditorElement {
  const editor = document.createElement('qds-inline-rich-text') as InlineSurfaceEditorElement;
  editor.property = property;
  editor.value = sanitizeHtml(value);
  editor.placeholder = options.placeholder || '';
  return editor;
}

function replaceSurfaceContentWithEditor(
  target: HTMLElement | null,
  editor: InlineSurfaceEditorElement
): void {
  if (!target) {
    return;
  }

  target.textContent = '';
  target.appendChild(editor);
}

function createUnsupportedBlockSurface(
  block: QdsContentBlockInstance,
  reason: string
): HTMLElement {
  const surface = document.createElement('div');
  surface.className = 'qds-content-built-in qds-content-built-in--unsupported';
  surface.innerHTML = `
    <div class="qds-content-unsupported__eyebrow">Unsupported Block</div>
    <div class="qds-content-unsupported__title">${escapeHtml(block.type)}</div>
    <p class="qds-content-unsupported__body">${escapeHtml(reason)}</p>
  `;
  return surface;
}

function createRichTextSurface(block: QdsContentBlockInstance): HTMLElement {
  const surface = document.createElement('div');
  surface.className = 'qds-content-built-in qds-content-built-in--rich-text';
  surface.innerHTML = sanitizeHtml(String(block.config.html || ''));
  return surface;
}

function createEditableRichTextSurface(block: QdsContentBlockInstance): HTMLElement {
  const surface = document.createElement('div');
  surface.className = 'qds-content-built-in qds-content-built-in--rich-text';
  const editor = createInlineRichTextEditorElement(
    'html',
    toStringValue(block.config.html),
    { placeholder: 'Start writing here.' }
  );
  editor.setAttribute('toolbar', 'full');
  editor.setAttribute('floating-toolbar', '');
  surface.appendChild(editor);
  return surface;
}

function createSectionSurface(
  block: QdsContentBlockInstance,
  renderMode: QdsRenderMode = 'runtime'
): HTMLElement {
  const surface = document.createElement('section');
  surface.className = 'qds-content-built-in qds-content-built-in--section';
  const eyebrow = toStringValue(block.config.eyebrow);
  const title = toStringValue(block.config.title);

  surface.innerHTML = `
    ${eyebrow ? `<p class="qds-content-section__eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
    ${title ? `<h3 class="qds-content-section__title">${escapeHtml(title)}</h3>` : ''}
    ${renderMode === 'design' ? '' : '<div class="qds-content-section__children" data-qds-child-container="flow"></div>'}
  `;

  return surface;
}

function createSplitSectionSurface(
  block: QdsContentBlockInstance,
  context?: QdsRenderContext,
  renderMode: QdsRenderMode = 'runtime'
): HTMLElement {
  const surface = document.createElement('section');
  const mediaSrc = toStringValue(block.config.mediaSrc);
  const resolvedMediaSrc = mediaSrc && context?.resolveAssetUrl ? context.resolveAssetUrl(mediaSrc) : mediaSrc;
  const mediaPosition = toStringValue(block.config.mediaPosition, 'right') === 'left' ? 'left' : 'right';
  const mediaFit = toStringValue(block.config.mediaFit, 'cover') === 'contain' ? 'contain' : 'cover';
  const hasMedia = Boolean(resolvedMediaSrc);
  const contentMarkup = renderMode === 'design'
    ? ''
    : '<div class="qds-content-split-section__content" data-qds-child-container="flow"></div>';
  const mediaMarkup = hasMedia
    ? `
      <div class="qds-content-split-section__media">
        <img
          class="qds-content-split-section__media-image"
          src="${escapeHtml(resolvedMediaSrc)}"
          alt="${escapeHtml(toStringValue(block.config.mediaAlt))}"
        >
      </div>
    `
    : '';

  surface.className = [
    'qds-content-built-in',
    'qds-content-built-in--split-section',
    `qds-content-built-in--split-section-${mediaPosition}`,
    hasMedia ? '' : 'qds-content-built-in--split-section-no-media'
  ]
    .filter(Boolean)
    .join(' ');

  if (hasMedia) {
    surface.innerHTML = mediaPosition === 'left'
      ? `${mediaMarkup}${contentMarkup}`
      : `${contentMarkup}${mediaMarkup}`;
  } else {
    surface.innerHTML = contentMarkup;
  }

  const mediaImage = surface.querySelector<HTMLElement>('.qds-content-split-section__media-image');
  if (mediaImage) {
    mediaImage.style.objectFit = mediaFit;
  }

  return surface;
}

function createHeadingSurface(block: QdsContentBlockInstance): HTMLElement {
  const level = Math.min(6, Math.max(1, toNumber(block.config.level, 2)));
  const heading = document.createElement(`h${level}`);
  heading.className = 'qds-content-built-in qds-content-built-in--heading';
  heading.style.textAlign = toStringValue(block.config.align, 'left');
  heading.textContent = toStringValue(block.config.text, block.title || 'Heading');
  return heading;
}

function createCalloutSurface(block: QdsContentBlockInstance): HTMLElement {
  const surface = document.createElement('section');
  const tone = toStringValue(block.config.tone, 'info');
  surface.className = `qds-content-built-in qds-content-built-in--callout qds-content-built-in--tone-${tone}`;
  const title = toStringValue(block.config.title, block.title || 'Callout');
  const body = toStringValue(block.config.body);

  surface.innerHTML = `
    <div class="qds-content-callout__tone">${escapeHtml(tone)}</div>
    <h3 class="qds-content-callout__title">${escapeHtml(title)}</h3>
    <p class="qds-content-callout__body">${escapeHtml(body)}</p>
  `;

  return surface;
}

function createCodeBlockSurface(block: QdsContentBlockInstance): HTMLElement {
  const element = document.createElement('qds-code-block') as HTMLElement & {
    value?: string;
    language?: string;
  };
  element.language = toStringValue(block.config.language, 'text');
  element.value = toStringValue(block.config.value);
  return element;
}

function createHeroSurface(
  block: QdsContentBlockInstance,
  context?: QdsRenderContext
): HTMLElement {
  const element = document.createElement('qds-hero');
  const mediaSrc = toStringValue(block.config.mediaSrc);
  const primaryHref = toStringValue(block.config.primaryHref);
  const secondaryHref = toStringValue(block.config.secondaryHref);

  element.setAttribute('eyebrow', toStringValue(block.config.eyebrow));
  element.setAttribute('heading', toStringValue(block.config.title, block.title || 'Hero'));
  element.setAttribute('body', toStringValue(block.config.body));
  element.setAttribute('media-src', mediaSrc && context?.resolveAssetUrl ? context.resolveAssetUrl(mediaSrc) : mediaSrc);
  element.setAttribute('media-alt', toStringValue(block.config.mediaAlt));
  element.setAttribute('primary-label', toStringValue(block.config.primaryLabel));
  element.setAttribute('primary-href', resolveSafeLink(primaryHref, context));
  element.setAttribute('primary-target', toStringValue(block.config.primaryTarget, '_self'));
  element.setAttribute('secondary-label', toStringValue(block.config.secondaryLabel));
  element.setAttribute('secondary-href', resolveSafeLink(secondaryHref, context));
  element.setAttribute('secondary-target', toStringValue(block.config.secondaryTarget, '_self'));
  element.setAttribute('align', toStringValue(block.config.align, 'left'));
  return element;
}

function createImageSurface(
  block: QdsContentBlockInstance,
  context?: QdsRenderContext
): HTMLElement {
  const src = toStringValue(block.config.src);
  const href = toStringValue(block.config.href);
  const resolvedSrc = context?.resolveAssetUrl ? context.resolveAssetUrl(src) : src;
  const resolvedHref = resolveSafeLink(href, context);
  const fit = toStringValue(block.config.fit, 'cover');
  const caption = toStringValue(block.config.caption);
  const image = document.createElement('img');
  image.src = resolvedSrc;
  image.alt = toStringValue(block.config.alt, '');
  image.className = 'qds-content-image__img';
  image.style.objectFit = fit;

  const figure = document.createElement('figure');
  figure.className = 'qds-content-built-in qds-content-built-in--image';
  const mediaShell = resolvedHref ? document.createElement('a') : document.createElement('div');

  mediaShell.className = 'qds-content-image__media';
  if (resolvedHref && mediaShell instanceof HTMLAnchorElement) {
    mediaShell.href = resolvedHref;
  }

  mediaShell.appendChild(image);
  figure.appendChild(mediaShell);

  if (caption) {
    const figcaption = document.createElement('figcaption');
    figcaption.className = 'qds-content-image__caption';
    figcaption.textContent = caption;
    figure.appendChild(figcaption);
  }

  return figure;
}

function createLinkListSurface(
  block: QdsContentBlockInstance,
  context?: QdsRenderContext
): HTMLElement {
  const links = parseStructuredConfigValue<Array<Record<string, unknown>>>(block.config.links, []);
  const section = document.createElement('section');
  section.className = 'qds-content-built-in qds-content-built-in--link-list';
  const title = toStringValue(block.config.title);

  section.innerHTML = `
    ${title ? `<h3 class="qds-content-link-list__title">${escapeHtml(title)}</h3>` : ''}
    <ul class="qds-content-link-list__items"></ul>
  `;

  const list = section.querySelector('.qds-content-link-list__items') as HTMLUListElement;

  links.forEach((entry) => {
    const href = toStringValue(entry.href);
    const resolvedHref = resolveSafeLink(href, context);
    const item = document.createElement('li');
    item.className = 'qds-content-link-list__item';
    item.innerHTML = `
      <a class="qds-content-link-list__anchor" href="${escapeHtml(resolvedHref || '#')}">${escapeHtml(toStringValue(entry.label, 'Untitled link'))}</a>
      ${entry.description ? `<p class="qds-content-link-list__description">${escapeHtml(toStringValue(entry.description))}</p>` : ''}
    `;
    list.appendChild(item);
  });

  return section;
}

function createGradientLineSurface(block: QdsContentBlockInstance): HTMLElement {
  const element = document.createElement('qds-gradient-line');
  element.setAttribute('start-color', toStringValue(block.config.startColor, 'var(--theme-success, var(--brand-green))'));
  element.setAttribute('end-color', toStringValue(block.config.endColor, 'var(--theme-accent-primary, var(--brand-middle-blue))'));
  element.setAttribute('height', String(Math.max(1, toNumber(block.config.height, 3))));
  return element;
}

function createDiagramSurface(block: QdsContentBlockInstance): HTMLElement {
  const element = document.createElement('qds-diagram-host');
  const diagram = toStringValue(block.config.diagram);
  const path = toStringValue(block.config.path);
  const drawingName = toStringValue(block.config.drawingName);
  const height = toStringValue(block.config.height, '420px');
  const libraries = toStringValue(block.config.libraries, 'Quantum');

  element.setAttribute('width', '100%');
  element.setAttribute('height', height || '420px');
  element.setAttribute('editable', String(Boolean(block.config.editable)));

  if (diagram) {
    element.setAttribute('diagram', diagram);
  }

  if (path) {
    element.setAttribute('path', path);
  }

  if (drawingName) {
    element.setAttribute('drawing-name', drawingName);
  }

  if (libraries) {
    element.setAttribute('libraries', libraries);
  }

  return element;
}

function createCardBasicSurface(block: QdsContentBlockInstance): HTMLElement {
  const element = document.createElement('qds-card-basic');
  element.setAttribute('title', toStringValue(block.config.title, block.title || 'Card'));
  element.setAttribute('description', toStringValue(block.config.description));
  element.setAttribute('icon', toStringValue(block.config.icon));
  element.setAttribute('button-text', toStringValue(block.config.buttonText, 'Browse'));
  element.setAttribute('href', sanitizeUrl(toStringValue(block.config.href, '#')) || '#');
  element.setAttribute('target', toStringValue(block.config.target, '_self'));
  return element;
}

function createCardAdvancedSurface(block: QdsContentBlockInstance): HTMLElement {
  const element = document.createElement('qds-card-advanced');
  element.setAttribute('title', toStringValue(block.config.title, block.title || 'Card'));
  element.setAttribute('description', toStringValue(block.config.description));
  element.setAttribute('image', toStringValue(block.config.image));
  element.setAttribute('button-text', toStringValue(block.config.buttonText, 'More'));
  element.setAttribute('href', sanitizeUrl(toStringValue(block.config.href, '#')) || '#');
  element.setAttribute('target', toStringValue(block.config.target, '_self'));
  element.setAttribute('image-position', toStringValue(block.config.imagePosition, 'top'));
  return element;
}

function createCardGridSurface(
  block: QdsContentBlockInstance,
  _context?: QdsRenderContext,
  renderMode: QdsRenderMode = 'runtime'
): HTMLElement {
  const columns = Math.max(1, Math.min(4, toNumber(block.config.columns, 3)));
  const section = document.createElement('section');
  section.className = 'qds-content-built-in qds-content-built-in--card-grid';
  section.style.setProperty('--qds-content-card-columns', String(columns));
  const title = toStringValue(block.config.title);

  section.innerHTML = `
    ${title ? `<h3 class="qds-content-card-grid__title">${escapeHtml(title)}</h3>` : ''}
    ${renderMode === 'design' ? '' : '<div class="qds-content-card-grid__grid" data-qds-child-container="grid"></div>'}
  `;

  return section;
}

function enhanceSurfaceForDesignEditing(
  surface: HTMLElement,
  block: QdsContentBlockInstance
): HTMLElement {
  switch (block.type) {
    case 'section':
      replaceSurfaceContentWithEditor(
        surface.querySelector<HTMLElement>('.qds-content-section__eyebrow'),
        createInlineTextEditorElement('eyebrow', toStringValue(block.config.eyebrow), {
          placeholder: 'Section'
        })
      );
      replaceSurfaceContentWithEditor(
        surface.querySelector<HTMLElement>('.qds-content-section__title'),
        createInlineTextEditorElement('title', toStringValue(block.config.title), {
          placeholder: 'Section title'
        })
      );
      return surface;

    case 'heading':
      replaceSurfaceContentWithEditor(
        surface,
        createInlineTextEditorElement('text', toStringValue(block.config.text, block.title || 'Heading'), {
          placeholder: 'Section heading'
        })
      );
      return surface;

    case 'callout':
      replaceSurfaceContentWithEditor(
        surface.querySelector<HTMLElement>('.qds-content-callout__title'),
        createInlineTextEditorElement('title', toStringValue(block.config.title, block.title || 'Callout'), {
          placeholder: 'Callout title'
        })
      );
      return surface;

    case 'image':
      replaceSurfaceContentWithEditor(
        surface.querySelector<HTMLElement>('.qds-content-image__caption'),
        createInlineTextEditorElement('caption', toStringValue(block.config.caption), {
          placeholder: 'Optional supporting caption',
          multiline: true
        })
      );
      return surface;

    case 'link-list':
      replaceSurfaceContentWithEditor(
        surface.querySelector<HTMLElement>('.qds-content-link-list__title'),
        createInlineTextEditorElement('title', toStringValue(block.config.title), {
          placeholder: 'Quick links'
        })
      );
      return surface;

    case 'card-grid':
      replaceSurfaceContentWithEditor(
        surface.querySelector<HTMLElement>('.qds-content-card-grid__title'),
        createInlineTextEditorElement('title', toStringValue(block.config.title), {
          placeholder: 'Highlights'
        })
      );
      return surface;

    default:
      return surface;
  }
}

function applyBlockFontStyles(
  surface: HTMLElement,
  block: QdsContentBlockInstance
): void {
  switch (block.type) {
    case 'section':
      applyTypographyStyle(surface.querySelector<HTMLElement>('.qds-content-section__eyebrow'), block.config.eyebrowFont);
      applyTypographyStyle(surface.querySelector<HTMLElement>('.qds-content-section__title'), block.config.titleFont);
      return;

    case 'split-section':
      return;

    case 'heading':
      applyTypographyStyle(surface, block.config.textFont);
      return;

    case 'callout':
      applyTypographyStyle(surface.querySelector<HTMLElement>('.qds-content-callout__title'), block.config.titleFont);
      applyTypographyStyle(surface.querySelector<HTMLElement>('.qds-content-callout__body'), block.config.bodyFont);
      return;

    case 'hero':
      (surface as HTMLElement & { typography?: Record<string, unknown> }).typography = {
        eyebrow: block.config.eyebrowFont,
        title: block.config.titleFont,
        body: block.config.bodyFont,
        primaryAction: block.config.primaryLabelFont,
        secondaryAction: block.config.secondaryLabelFont
      };
      return;

    case 'image':
      applyTypographyStyle(surface.querySelector<HTMLElement>('.qds-content-image__caption'), block.config.captionFont);
      return;

    case 'link-list':
      applyTypographyStyle(surface.querySelector<HTMLElement>('.qds-content-link-list__title'), block.config.titleFont);
      return;

    case 'card-basic':
      (surface as HTMLElement & { typography?: Record<string, unknown> }).typography = {
        title: block.config.titleFont,
        description: block.config.descriptionFont,
        buttonText: block.config.buttonTextFont
      };
      return;

    case 'card-advanced':
      (surface as HTMLElement & { typography?: Record<string, unknown> }).typography = {
        title: block.config.titleFont,
        description: block.config.descriptionFont,
        buttonText: block.config.buttonTextFont
      };
      return;

    case 'card-grid':
      applyTypographyStyle(surface.querySelector<HTMLElement>('.qds-content-card-grid__title'), block.config.titleFont);
      return;

    default:
      return;
  }
}

function createBuiltInSurface(
  block: QdsContentBlockInstance,
  context?: QdsRenderContext,
  renderMode: QdsRenderMode = 'runtime',
  selected = false,
  readonly = false
): HTMLElement {
  if (block.type === 'rich-text' && shouldRenderCanvasEditor(renderMode, selected, readonly)) {
    return createEditableRichTextSurface(block);
  }

  const shouldEnhanceForDesign = shouldRenderCanvasEditor(renderMode, selected, readonly);

  switch (block.type) {
    case 'rich-text':
      return createRichTextSurface(block);
    case 'section':
      return shouldEnhanceForDesign
        ? enhanceSurfaceForDesignEditing(createSectionSurface(block, renderMode), block)
        : createSectionSurface(block, renderMode);
    case 'split-section':
      return createSplitSectionSurface(block, context, renderMode);
    case 'heading':
      return shouldEnhanceForDesign
        ? enhanceSurfaceForDesignEditing(createHeadingSurface(block), block)
        : createHeadingSurface(block);
    case 'callout':
      return shouldEnhanceForDesign
        ? enhanceSurfaceForDesignEditing(createCalloutSurface(block), block)
        : createCalloutSurface(block);
    case 'code-block':
      return createCodeBlockSurface(block);
    case 'hero':
      return createHeroSurface(block, context);
    case 'image':
      return shouldEnhanceForDesign
        ? enhanceSurfaceForDesignEditing(createImageSurface(block, context), block)
        : createImageSurface(block, context);
    case 'diagram':
      return createDiagramSurface(block);
    case 'gradient-line':
      return createGradientLineSurface(block);
    case 'card-basic':
      return createCardBasicSurface(block);
    case 'card-advanced':
      return createCardAdvancedSurface(block);
    case 'link-list':
      return shouldEnhanceForDesign
        ? enhanceSurfaceForDesignEditing(createLinkListSurface(block, context), block)
        : createLinkListSurface(block, context);
    case 'card-grid':
      return shouldEnhanceForDesign
        ? enhanceSurfaceForDesignEditing(createCardGridSurface(block, context, renderMode), block)
        : createCardGridSurface(block, context, renderMode);
    default:
      return createUnsupportedBlockSurface(block, 'No built-in renderer is registered for this block type.');
  }
}

function isBuiltInBlock(definition: QdsBlockDefinition): boolean {
  return !definition.renderTag;
}

export interface MountContentBlockOptions {
  block: QdsContentBlockInstance;
  contentDocument: QdsContentDocument;
  registry?: QdsBlockRegistry | null;
  context?: QdsRenderContext;
  renderMode?: QdsRenderMode;
  breakpoint?: QdsContentBreakpoint;
  readonly?: boolean;
  selected?: boolean;
}

function mountNestedBlockChildren(
  container: HTMLElement,
  options: MountContentBlockOptions
): void {
  const registry = resolveRegistry(options.registry || options.context?.blockRegistry || null);
  const children = getBlockChildren(options.block, 'page')
    .filter((child) => options.breakpoint ? isBlockVisible(child, options.breakpoint) : true);

  children.forEach((child) => {
    const childMount = document.createElement('div');
    childMount.className = options.block.type === 'card-grid'
      ? 'qds-content-card-grid__card'
      : 'qds-content-section__child';
    container.appendChild(childMount);
    mountContentBlock(childMount, {
      ...options,
      block: child,
      registry
    });
  });
}

function bindInlineSurfaceEditors(
  container: HTMLElement,
  surface: HTMLElement,
  options: MountContentBlockOptions
): void {
  if (options.renderMode !== 'design') {
    return;
  }

  surface.addEventListener('value-change', (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (!target || (target.tagName !== 'QDS-INLINE-TEXT-EDITOR' && target.tagName !== 'QDS-INLINE-RICH-TEXT')) {
      return;
    }

    const detail = (event as CustomEvent<{ property?: string; value?: unknown }>).detail || {};
    const propertyName = typeof detail.property === 'string' && detail.property
      ? detail.property
      : target.getAttribute('property') || '';

    if (!propertyName) {
      return;
    }

    event.stopPropagation();
    const rawValue = detail.value;
    const nextValue = target.tagName === 'QDS-INLINE-RICH-TEXT'
      ? sanitizeHtml(toStringValue(rawValue))
      : rawValue;

    container.dispatchEvent(new CustomEvent('block-config-change', {
      bubbles: true,
      composed: true,
      detail: {
        blockId: options.block.id,
        config: {
          ...options.block.config,
          [propertyName]: nextValue
        }
      }
    }));
  });
}

export function mountContentBlock(
  container: HTMLElement,
  options: MountContentBlockOptions
): void {
  const registry = resolveRegistry(options.registry || options.context?.blockRegistry || null);
  const definition = registry.get(options.block.type);
  container.innerHTML = '';

  if (!definition) {
    container.appendChild(
      createUnsupportedBlockSurface(
        options.block,
        'The current registry does not define this block type.'
      )
    );
    return;
  }

  if (isBuiltInBlock(definition)) {
    const surface = createBuiltInSurface(
      options.block,
      options.context,
      options.renderMode || 'runtime',
      Boolean(options.selected),
      Boolean(options.readonly)
    );
    applyBlockFontStyles(surface, options.block);
    container.appendChild(surface);
    bindInlineSurfaceEditors(container, surface, options);

    if (options.renderMode !== 'design') {
      const childMount = surface.querySelector<HTMLElement>('[data-qds-child-container]');
      if (childMount) {
        mountNestedBlockChildren(childMount, options);
      }
    }
    return;
  }

  if (!definition.renderTag || !definition.renderTag.includes('-') || !customElements.get(definition.renderTag)) {
    container.appendChild(
      createUnsupportedBlockSurface(
        options.block,
        `The host renderer "${definition.renderTag || 'unknown'}" is not available.`
      )
    );
    return;
  }

  const hostElement = document.createElement(definition.renderTag) as QdsHostBlockElement;
  hostElement.block = deepClone(options.block);
  hostElement.config = deepClone(options.block.config);
  hostElement.context = options.context;
  hostElement.contentDocument = deepClone(options.contentDocument);
  hostElement.layout = options.block.layout ? { ...options.block.layout } : undefined;
  hostElement.renderMode = options.renderMode || 'runtime';
  hostElement.readonly = options.readonly;
  hostElement.selected = options.selected;
  hostElement.classList.add('qds-content-host-block');
  container.appendChild(hostElement);
}

export function getContentBlockDisplayName(
  block: QdsContentBlockInstance,
  registry?: QdsBlockRegistry | null
): string {
  const definition = resolveRegistry(registry).get(block.type);
  const configTitle = toStringValue(block.config.title);
  return block.title || configTitle || definition?.label || block.type;
}

export function getContentRendererStyles(): string {
  return `
    :host {
      display: block;
      color: var(--theme-text-primary, #333333);
      font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
      font-size: var(--font-size-body, 14px);
      line-height: 1.5;
    }

    .qds-content-built-in {
      color: inherit;
    }

    .qds-content-built-in--unsupported {
      padding: 16px;
      border: 1px dashed var(--theme-border-color, #e0e0e0);
      border-radius: 8px;
      background: var(--theme-background-secondary, #f8f8f8);
    }

    .qds-content-unsupported__eyebrow,
    .qds-content-callout__tone,
    .qds-content-section__eyebrow,
    .qds-content-card-grid__eyebrow {
      margin: 0 0 8px;
      color: var(--theme-text-secondary, #5f6368);
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .qds-content-unsupported__title,
    .qds-content-section__title,
    .qds-content-callout__title,
    .qds-content-link-list__title,
    .qds-content-card-grid__title {
      margin: 0 0 12px;
      color: var(--theme-text-primary, #333333);
      font-size: clamp(1rem, 1vw + 0.85rem, 1.35rem);
      font-weight: 600;
    }

    .qds-content-unsupported__body,
    .qds-content-callout__body,
    .qds-content-link-list__description,
    .qds-content-card-grid__description,
    .qds-content-image__caption {
      margin: 0;
      color: var(--theme-text-secondary, #5f6368);
    }

    .qds-content-built-in--section {
      padding: 20px;
      border: 1px solid var(--theme-border-color, #e0e0e0);
      border-radius: 8px;
      background: var(--theme-background-primary, #ffffff);
      box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
    }

    .qds-content-section__children {
      display: grid;
      gap: 16px;
      margin-top: 16px;
    }

    .qds-content-built-in--split-section {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 0;
      align-items: stretch;
    }

    .qds-content-built-in--split-section-left {
      grid-auto-flow: dense;
    }

    .qds-content-built-in--split-section-no-media {
      grid-template-columns: minmax(0, 1fr);
    }

    .qds-content-split-section__content {
      display: grid;
      gap: 16px;
      min-width: 0;
      height: 100%;
      align-content: stretch;
    }

    .qds-content-split-section__media {
      display: block;
      height: 100%;
      min-width: 0;
      overflow: hidden;
      border-radius: 12px;
      border: 1px solid var(--theme-border-color, #e0e0e0);
      background: var(--theme-background-secondary, #f8f8f8);
      box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
    }

    .qds-content-split-section__media-image {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 280px;
      max-height: none;
      background: var(--theme-background-secondary, #f8f8f8);
    }

    .qds-content-section__child {
      min-width: 0;
    }

    .qds-content-split-section__content > .qds-content-section__child,
    .qds-content-split-section__content > .qds-content-section__child > .qds-content-host-block {
      height: 100%;
    }

    .qds-content-built-in--split-section-right .qds-content-split-section__media {
      border-radius: 0 12px 12px 0;
      border-left-width: 0;
    }

    .qds-content-built-in--split-section-left .qds-content-split-section__media {
      border-radius: 12px 0 0 12px;
      border-right-width: 0;
    }

    .qds-content-built-in--split-section-right .qds-content-split-section__content > .qds-content-section__child > qds-action-panel {
      --qds-action-panel-host-height: 100%;
      --qds-action-panel-min-height: 100%;
      --qds-action-panel-max-width: 100%;
      --qds-action-panel-margin: 0;
      --qds-action-panel-border-radius: 12px 0 0 12px;
      --qds-action-panel-border-right-width: 0;
    }

    .qds-content-built-in--split-section-left .qds-content-split-section__content > .qds-content-section__child > qds-action-panel {
      --qds-action-panel-host-height: 100%;
      --qds-action-panel-min-height: 100%;
      --qds-action-panel-max-width: 100%;
      --qds-action-panel-margin: 0;
      --qds-action-panel-border-radius: 0 12px 12px 0;
      --qds-action-panel-border-left-width: 0;
    }

    .qds-content-built-in--rich-text > :first-child {
      margin-top: 0;
    }

    .qds-content-built-in--rich-text > :last-child {
      margin-bottom: 0;
    }

    .qds-content-built-in--heading {
      margin: 0;
      color: var(--theme-text-primary, #333333);
      font-size: clamp(1.15rem, 1vw + 1rem, 1.75rem);
      font-weight: 600;
    }

    .qds-content-built-in--callout {
      padding: 18px;
      border-radius: 8px;
      border: 1px solid var(--theme-border-color, #e0e0e0);
      box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
    }

    .qds-content-built-in--tone-info {
      background: rgba(66, 133, 244, 0.08);
      border-color: rgba(66, 133, 244, 0.25);
    }

    .qds-content-built-in--tone-success {
      background: rgba(52, 168, 83, 0.08);
      border-color: rgba(52, 168, 83, 0.25);
    }

    .qds-content-built-in--tone-warning {
      background: rgba(251, 188, 4, 0.12);
      border-color: rgba(251, 188, 4, 0.3);
    }

    .qds-content-built-in--tone-critical {
      background: rgba(234, 67, 53, 0.08);
      border-color: rgba(234, 67, 53, 0.25);
    }

    .qds-content-built-in--image {
      margin: 0;
      display: grid;
      gap: 12px;
    }

    .qds-content-image__media {
      display: block;
      overflow: hidden;
      border-radius: 12px;
      border: 1px solid var(--theme-border-color, #e0e0e0);
      background: var(--theme-background-secondary, #f8f8f8);
    }

    .qds-content-image__img {
      display: block;
      width: 100%;
      min-height: 180px;
      max-height: 520px;
    }

    .qds-content-built-in--link-list ul {
      margin: 0;
      padding: 0;
      list-style: none;
      display: grid;
      gap: 12px;
    }

    .qds-content-link-list__item {
      padding: 14px 16px;
      border: 1px solid var(--theme-border-color, #e0e0e0);
      border-radius: 8px;
      background: var(--theme-background-primary, #ffffff);
    }

    .qds-content-link-list__anchor {
      color: var(--input-focus-border, #4285f4);
      text-decoration: none;
    }

    .qds-content-link-list__anchor:hover,
    .qds-content-link-list__anchor:visited,
    .qds-content-link-list__anchor:focus-visible {
      color: var(--input-focus-border, #4285f4);
    }

    .qds-content-link-list__anchor:hover,
    .qds-content-link-list__anchor:focus-visible {
      text-decoration: underline;
    }

    .qds-content-card-grid__card {
      color: inherit;
      text-decoration: none;
    }

    .qds-content-card-grid__card:hover {
      text-decoration: none;
    }

    .qds-content-card-grid__grid {
      display: grid;
      grid-template-columns: repeat(var(--qds-content-card-columns, 3), minmax(0, 1fr));
      gap: 16px;
    }

    .qds-content-card-grid__card {
      display: block;
      min-width: 0;
    }

    .qds-content-host-block {
      display: block;
      width: 100%;
    }

    @media (max-width: 1200px) {
      .qds-content-built-in--split-section {
        grid-template-columns: minmax(0, 1fr);
        gap: 16px;
      }

      .qds-content-built-in--split-section-right .qds-content-split-section__media,
      .qds-content-built-in--split-section-left .qds-content-split-section__media {
        border-radius: 12px;
        border-left-width: 1px;
        border-right-width: 1px;
      }

      .qds-content-built-in--split-section-right .qds-content-split-section__content > .qds-content-section__child > qds-action-panel,
      .qds-content-built-in--split-section-left .qds-content-split-section__content > .qds-content-section__child > qds-action-panel {
        --qds-action-panel-border-radius: 12px;
        --qds-action-panel-border-right-width: var(--qds-action-panel-border-width, 3px);
        --qds-action-panel-border-left-width: var(--qds-action-panel-border-width, 3px);
      }

      .qds-content-card-grid__grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 720px) {
      .qds-content-card-grid__grid {
        grid-template-columns: minmax(0, 1fr);
      }
    }
  `;
}

export function getDefinitionField(
  definition: QdsBlockDefinition | undefined,
  propertyName: string
): QdsConfigField | undefined {
  return definition?.configSchema?.[propertyName];
}
